// Schema-bound chart widget — line / bar / area / pie via recharts.
// Accepts:
//   props.data:     Array<Record<string, unknown>>            — required
//   props.kind:     "line" | "bar" | "area" | "pie"           — default "line"
//   props.x:        string                                    — x-axis key
//   props.y:        string | string[]                         — series key(s)
//   props.colors:   string[]                                  — optional palette override

import { useMemo } from "react";
import {
  ResponsiveContainer,
  LineChart, Line,
  BarChart, Bar,
  AreaChart, Area,
  PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";
import type { WidgetComponent } from "@viz/presentation";

type Row = Record<string, unknown>;

const DEFAULT_PALETTE = ["#0ea5e9", "#a855f7", "#f97316", "#22c55e", "#eab308", "#ec4899", "#14b8a6", "#f43f5e"];

const ChartWidget: WidgetComponent = ({ props }) => {
  const kind = (props.kind as string | undefined) ?? "line";
  const data = (props.data as Row[] | undefined) ?? [];
  const x = (props.x as string | undefined) ?? "x";
  const yRaw = props.y as string | string[] | undefined;
  const ys = useMemo(() => Array.isArray(yRaw) ? yRaw : (yRaw ? [yRaw] : inferSeries(data, x)), [yRaw, data, x]);
  const colors = (props.colors as string[] | undefined) ?? DEFAULT_PALETTE;
  const height = (props.height as number | string | undefined) ?? 320;

  if (data.length === 0) {
    return <div className="grid h-full min-h-[160px] place-items-center text-sm text-slate-500">No data.</div>;
  }

  const tooltipStyle = { background: "#0f172a", border: "1px solid #334155", color: "#e2e8f0" };

  if (kind === "pie") {
    const valueKey = ys[0] ?? "value";
    return (
      <div style={{ width: "100%", height }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie data={data} dataKey={valueKey} nameKey={x} outerRadius="80%" labelLine={false}>
              {data.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
            </Pie>
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (kind === "bar") {
    return (
      <div style={{ width: "100%", height }}>
        <ResponsiveContainer>
          <BarChart data={data}>
            <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
            <XAxis dataKey={x} stroke="#94a3b8" fontSize={11} />
            <YAxis stroke="#94a3b8" fontSize={11} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
            {ys.map((k, i) => <Bar key={k} dataKey={k} fill={colors[i % colors.length]} />)}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (kind === "area") {
    return (
      <div style={{ width: "100%", height }}>
        <ResponsiveContainer>
          <AreaChart data={data}>
            <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
            <XAxis dataKey={x} stroke="#94a3b8" fontSize={11} />
            <YAxis stroke="#94a3b8" fontSize={11} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
            {ys.map((k, i) => (
              <Area key={k} type="monotone" dataKey={k} stroke={colors[i % colors.length]} fill={colors[i % colors.length]} fillOpacity={0.25} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // default: line
  return (
    <div style={{ width: "100%", height }}>
      <ResponsiveContainer>
        <LineChart data={data}>
          <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
          <XAxis dataKey={x} stroke="#94a3b8" fontSize={11} />
          <YAxis stroke="#94a3b8" fontSize={11} />
          <Tooltip contentStyle={tooltipStyle} />
          <Legend />
          {ys.map((k, i) => (
            <Line key={k} type="monotone" dataKey={k} stroke={colors[i % colors.length]} dot={false} strokeWidth={2} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

function inferSeries(data: Row[], x: string): string[] {
  if (data.length === 0) return [];
  const first = data[0]!;
  const out: string[] = [];
  for (const k of Object.keys(first)) {
    if (k === x) continue;
    if (typeof first[k] === "number") out.push(k);
  }
  return out;
}

export default ChartWidget;
